/**
 * @file Contains phone number validation rules.
 *
 * NOTE: This file imports from libphonenumber-js which is a large dependency (~35KB gzipped).
 * Import directly from this file only when phone validation is needed to avoid
 * pulling the dependency into bundles that don't need it.
 */
import I18NService, { msg, msgf } from '@/services/isomorphic/I18NService';
import { validatePhoneNumber } from '@/services/utils/phone-utils';
import {
  InvalidPhoneNumberCountryError,
  PhoneNumberTooLongError,
  PhoneNumberTooShortError,
  UnableToParsePhoneNumberError
} from '@/services/utils/phone-utils/errors';
import type { IValidationResult, Validation } from '../validatable';
import { forms_error_phoneNumber_tooShort } from "@/lang/__generated__/ahnu/forms_error_phoneNumber_tooShort";
import { forms_error_phoneNumber_tooLong } from "@/lang/__generated__/ahnu/forms_error_phoneNumber_tooLong";
import { forms_error_phoneNumber_invalidCountryCode } from "@/lang/__generated__/ahnu/forms_error_phoneNumber_invalidCountryCode";
import { forms_error_phoneNumber_default } from "@/lang/__generated__/ahnu/forms_error_phoneNumber_default";
import { forms_error_validPhoneNumber } from "@/lang/__generated__/ahnu/forms_error_validPhoneNumber";

/**
 * Validates that the input is a valid phone number.
 *
 * @param name - Resource string.
 * @param property - Property key.
 * @param value - The value to validate.
 *
 * @returns Whether the property is valid and an error message.
 * @throws
 */
export const validPhoneNumber: Validation<string> = (
  name: string,
  property: string,
  value: string
): IValidationResult => {
  // If an empty string is passed...
  if (value === '') {
    // Return the "enter a valid phone number" message.
    return {
      isValid: false,
      errorMessage: msgf(forms_error_validPhoneNumber, {
        name: name.toLowerCase()
      })
    };
  }

  try {
    const isValid = validatePhoneNumber(
      value,
      I18NService.currentLocale.country
    );

    return {
      isValid,
      errorMessage: isValid
        ? null
        : msgf(forms_error_phoneNumber_default, { name: name.toLowerCase() })
    };
  } catch (error) {
    if (error instanceof UnableToParsePhoneNumberError) {
      let errorMessage;

      switch (true) {
        case error instanceof PhoneNumberTooShortError:
          errorMessage = msg(forms_error_phoneNumber_tooShort);
          break;
        case error instanceof PhoneNumberTooLongError:
          errorMessage = msg(forms_error_phoneNumber_tooLong);
          break;
        case error instanceof InvalidPhoneNumberCountryError:
          errorMessage = msg(forms_error_phoneNumber_invalidCountryCode);
          break;
        default:
          errorMessage = msg(forms_error_phoneNumber_default);
          break;
      }

      return { isValid: false, errorMessage };
    }

    throw error;
  }
};
